<script setup>
import { withTrailingSlash } from '@/utils/siteUrl'

defineProps({
  slug:    { type: String, required: true },
  title:   { type: String, required: true },
  excerpt: { type: String, required: true },
  image:   { type: String, default: '' },
})
</script>

<template>
  <RouterLink :to="withTrailingSlash(`/our-work/${slug}`)" class="our-work-card" :aria-label="title">
    <div class="image">
      <!-- Decorative: the visible <h3> title sits right beside it, so an alt
           duplicating the title would be redundant for screen readers. -->
      <img v-if="image" :src="image" alt="" loading="lazy" />
      <div v-else class="placeholder" />
    </div>
    <div class="body">
      <h3 class="title">{{ title }}</h3>
      <p class="excerpt">{{ excerpt }}</p>
      <span class="cta" aria-hidden="true">Find out more →</span>
    </div>
  </RouterLink>
</template>

<style lang="scss" scoped>
.our-work-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border);
  border-radius: $radius-lg;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  @include transition(box-shadow, background, transform);

  &:hover {
    background: var(--color-surface);
    box-shadow: 0 4px 6px rgba($color-ink, 0.16);
    transform: translateY(-2px);

    .cta { background-size: 100% 1px; }
  }
  &:focus-visible {
    @include focus-ring;
    .cta { background-size: 100% 1px; }
  }
  &:active { transform: translateY(1px); }

  .image {
    position: relative;
    width: 100%;
    aspect-ratio: 1.66 / 1; // flatter than the real 16:9 photos — narrower 3-col cards made the full 16:9 height feel oversized
    overflow: hidden;
    background: var(--color-fill);

    // Our Work items use landscape photos (not portrait report covers like
    // ResourceCard), so fill the panel edge-to-edge. Static — no hover zoom.
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    .placeholder {
      width: 100%;
      height: 100%;
    }
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: $space-2;
    padding: $space-5;
    flex: 1;
  }

  .title {
    margin: 0;
    font-size: $font-size-h4;
    font-weight: 600;
    color: var(--color-ink);
    line-height: 1.25;
  }

  .excerpt {
    font-size: $font-size-sm;
    color: var(--color-text);
    line-height: $lh-base;
    flex: 1;
  }
}
</style>
